import { useMutation } from "@apollo/client";
import { z } from "zod";
import type { FaEligibilityOutcome } from "@medbillai/generated-graphql-types";
import { Input, YStack } from "@medbillai/ui";
import { OnboardingScreen } from "../../components/OnboardingScreen";
import { type OnDone, tagged } from "../../lib/flow-engine";
import { useFormEngine, visibleFieldError } from "../../lib/form-engine";
import { checkFaEligibilityMutation } from "./utils/queries";
export type IncomeOutcomes = {
  checked: {
    outcome: FaEligibilityOutcome;
  };
  back: {};
};
const Outcome = tagged<IncomeOutcomes>();
const HELPER_TEXT = "Include your spouse's income if you file taxes jointly.";
const formatUsd = (income: number | undefined): string => income === undefined ? "" : income.toLocaleString("en-US");

// Whole annual dollars: the wire contract (CheckFaEligibilityInput.income Int)
// and the user.profile.householdIncome memory contract ("annual, digits only")
// are both dollar-denominated, so no cents here.
const INCOME_REQUIRED_MESSAGE = "Enter your annual income";
const incomeSchema = z.object({
  income: z.number({
    required_error: INCOME_REQUIRED_MESSAGE,
    invalid_type_error: INCOME_REQUIRED_MESSAGE
  }).int(INCOME_REQUIRED_MESSAGE).positive(INCOME_REQUIRED_MESSAGE)
});

// Partial: the schema describes the successful submit; the draft additionally
// holds the not-yet-answered state (`income: undefined`).
type IncomeFormValues = Partial<z.input<typeof incomeSchema>>;
export function Income({
  householdSize,
  onDone
}: {
  householdSize: number;
  onDone: OnDone<IncomeOutcomes>;
}) {
  const [checkFaEligibility] = useMutation(checkFaEligibilityMutation);
  const {
    form,
    formError,
    submit
  } = useFormEngine<IncomeFormValues, z.output<typeof incomeSchema>, FaEligibilityOutcome>({
    defaultValues: {
      income: undefined
    },
    schema: incomeSchema,
    // The schema's output type makes `income` a number here — no sentinel guard.
    onSubmit: async values => {
      // Throws bubble to the engine: Sentry capture + the generic form error.
      const {
        data
      } = await checkFaEligibility({
        variables: {
          input: {
            householdSize,
            income: values.income
          }
        }
      });
      const outcome = data?.checkFaEligibility.outcome;
      if (!outcome) {
        throw new Error("checkFaEligibility returned no outcome");
      }
      return {
        status: "ok",
        data: outcome
      };
    },
    onSubmitSuccess: (_values, {
      data: outcome
    }) => {
      onDone(Outcome.checked({
        outcome
      }));
    }
  });
  return <OnboardingScreen data-sentry-element="OnboardingScreen" data-sentry-component="Income" data-sentry-source-file="Income.tsx">
      <OnboardingScreen.Nav onBackPress={() => onDone(Outcome.back())} showLogo={false} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="Income.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="Income.tsx">
        <OnboardingScreen.Header alignment="left" title="What's your household annual income?" subtitle="Just add a rough estimate of your pre-tax income." data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="Income.tsx" />
        <YStack gap="$sp2" data-sentry-element="YStack" data-sentry-source-file="Income.tsx">
          <form.Field name="income" data-sentry-element="form.Field" data-sentry-source-file="Income.tsx">
            {field => {
            const errorMessage = visibleFieldError(field.state.meta);
            return <Input testID="income.amount-input" aria-label="Annual income" placeholder="Enter a rough estimate" prefix="$" inputMode="numeric" value={formatUsd(field.state.value)} onChangeText={text => {
              const digits = text.replace(/\D/g, "");
              field.handleChange(digits === "" ? undefined : Number(digits));
            }} onBlur={field.handleBlur} validation={errorMessage ? "error" : "none"} helperText={errorMessage ?? HELPER_TEXT} />;
          }}
          </form.Field>
        </YStack>
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="Income.tsx">
        <OnboardingScreen.FormError message={formError} data-sentry-element="OnboardingScreen.FormError" data-sentry-source-file="Income.tsx" />
        <form.Subscribe selector={state => state.isSubmitting} data-sentry-element="form.Subscribe" data-sentry-source-file="Income.tsx">
          {isSubmitting => <OnboardingScreen.PrimaryAction testID="income.check-eligibility" variant="brand" disabled={isSubmitting} loading={isSubmitting}
        // Always enabled: a tap on an incomplete form reveals what's
        // missing (submit errors route to the fields), never a dead button.
        onPress={() => submit()}>
              Check my eligibility
            </OnboardingScreen.PrimaryAction>}
        </form.Subscribe>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}